import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuthStore } from '../stores/authStore'
import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  mfaVerify as apiMfaVerify,
  mfaRecovery as apiMfaRecovery,
} from '../lib/auth'
import type { User } from '../types/user'

/** Role hierarchy from lowest to highest privilege */
const ROLE_HIERARCHY: User['role'][] = ['viewer', 'analyst', 'collection_manager', 'admin']

function getRoleLevel(role: string): number {
  const index = ROLE_HIERARCHY.indexOf(role as User['role'])
  return index >= 0 ? index : -1
}

export interface UseAuthReturn {
  /** Current authenticated user, or null */
  user: { id: string; email: string; name: string; role: string } | null
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Whether the initial auth check is in progress */
  isLoading: boolean
  /** Whether MFA verification is pending after login */
  mfaPending: boolean
  /** Login with email and password */
  login: (email: string, password: string) => Promise<void>
  /** Verify MFA TOTP code */
  verifyMfa: (code: string) => Promise<void>
  /** Verify MFA recovery code */
  verifyMfaRecovery: (code: string) => Promise<void>
  /** Logout and redirect to /login */
  logout: () => Promise<void>
  /** Check if user has a specific role */
  hasRole: (role: string) => boolean
  /** Check if user has at least this role level */
  hasMinRole: (minRole: string) => boolean
}

/**
 * Auth hook that wraps the Zustand auth store.
 *
 * On mount, it calls GET /api/v1/auth/me to check for an existing session.
 * Provides login/logout methods and role-checking utilities.
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout, hasRole } = useAuth()
 * ```
 */
export function useAuth(): UseAuthReturn {
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const setUser = useAuthStore((s) => s.setUser)
  const setLoading = useAuthStore((s) => s.setLoading)
  const storeLogout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const initialCheckDone = useRef(false)
  const [mfaPending, setMfaPending] = useState(false)

  // Initial auth check on mount
  useEffect(() => {
    if (initialCheckDone.current) return
    initialCheckDone.current = true

    let cancelled = false

    async function checkAuth() {
      try {
        setLoading(true)
        const authUser = await getCurrentUser()
        if (!cancelled) {
          setUser({
            id: authUser.id,
            email: authUser.email,
            name: authUser.name,
            role: authUser.role,
          })
        }
      } catch {
        if (!cancelled) {
          setUser(null)
        }
      }
    }

    checkAuth()

    return () => {
      cancelled = true
    }
  }, [setUser, setLoading])

  const login = useCallback(
    async (email: string, password: string) => {
      const authUser = await apiLogin({ email, password })
      // If the server signals MFA is required (via a specific response field or 403),
      // set mfaPending. Otherwise, complete login.
      setUser({
        id: authUser.id,
        email: authUser.email,
        name: authUser.name,
        role: authUser.role,
      })
    },
    [setUser],
  )

  const verifyMfa = useCallback(
    async (code: string) => {
      await apiMfaVerify(code)
      setMfaPending(false)
      // Re-fetch user to confirm full authentication.
      const authUser = await getCurrentUser()
      setUser({
        id: authUser.id,
        email: authUser.email,
        name: authUser.name,
        role: authUser.role,
      })
    },
    [setUser],
  )

  const verifyMfaRecovery = useCallback(
    async (code: string) => {
      await apiMfaRecovery(code)
      setMfaPending(false)
      const authUser = await getCurrentUser()
      setUser({
        id: authUser.id,
        email: authUser.email,
        name: authUser.name,
        role: authUser.role,
      })
    },
    [setUser],
  )

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch {
      // Even if the API call fails, clear local state
    }
    storeLogout()
    navigate({ to: '/login' })
  }, [storeLogout, navigate])

  const hasRole = useCallback(
    (role: string) => {
      if (!user) return false
      return user.role === role
    },
    [user],
  )

  const hasMinRole = useCallback(
    (minRole: string) => {
      if (!user) return false
      return getRoleLevel(user.role) >= getRoleLevel(minRole)
    },
    [user],
  )

  return {
    user,
    isAuthenticated,
    isLoading,
    mfaPending,
    login,
    verifyMfa,
    verifyMfaRecovery,
    logout,
    hasRole,
    hasMinRole,
  }
}
